import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  Spinner,
  Text,
  Tooltip,
} from '@fluentui/react-components';
import { CheckmarkRegular, CopyRegular, DismissRegular } from '@fluentui/react-icons';
import { triggerAdminConsent } from '../hooks/useAdminConsent';
import { useGeneralConfig } from '../hooks/useGeneralConfig';
import useEventTracker from '../hooks/useEventTracker';
import { ElementButton, PageName } from '../tracking/tracking.types';
import { customDisclaimerService } from '../services/customDisclaimerService';
import { policyService } from '../services/policyService';
import { formatDate } from '../utils/dateFormatters';
import { useDisclaimerEnrollmentSummary } from '../contexts/DisclaimerEnrollmentSummaryContext';
import { useStyles } from './CustomDisclaimers.style';

export const CUSTOM_DISCLAIMERS_REQUIRED_PERMISSIONS = [
  'Chat.Read.All',
 'ChatMessage.Send',
  'openid',
  'profile',
  'offline_access',
];

// Maximum number of resolved policy names shown before collapsing the rest behind an inline
// "+N" indicator (a separate total-count line always reports the full assigned count).
const MAX_VISIBLE_POLICY_NAMES = 4;

// formatDate's 'long' format always pads the day to two digits (e.g. "July 09, 2026");
// the required display copy has no leading zero (e.g. "July 9, 2026").
const formatUpdatedAt = (value: string) => formatDate(value, { format: 'long' }).replace(/ 0(\d),/, ' $1,');

const getEnrollmentLinkPreview = (value: string) => {
  try {
    return `${new URL(value).origin}/…`;
  } catch {
    return 'Enrollment link ready';
  }
};

export const CustomDisclaimers: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const eventTracker = useEventTracker();
  const { config, loadingConfig, loadConfig } = useGeneralConfig();

  const {
    disclaimers,
    disclaimerState,
    refreshDisclaimers,
    enrollmentState,
    usersReceivingByDisclaimerId,
    refreshEnrollmentStatus,
  } = useDisclaimerEnrollmentSummary();

  const [policyNamesById, setPolicyNamesById] = useState<Map<string, string>>(new Map());
  const [policiesLoading, setPoliciesLoading] = useState(false);
  const [policiesError, setPoliciesError] = useState<string | null>(null);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [enrollmentLink, setEnrollmentLink] = useState<string | null>(null);
  const [enrollmentLoading, setEnrollmentLoading] = useState(false);
  const [enrollmentError, setEnrollmentError] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [copySuccessMessage, setCopySuccessMessage] = useState<string | null>(null);

  // Reopening the dialog (or retrying) before an earlier fetch settles can leave a slower,
  // stale request racing a newer one. Each fetch stamps a token here; a response only commits
  // state (success or error) if its token is still the latest, so a stale response can never
  // clobber a newer attempt's result.
  const enrollmentRequestRef = useRef(0);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    eventTracker.eventTrackOnAppLaunched({ pageName: PageName.CUSTOM_DISCLAIMERS });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const azure = config?.applications?.backend?.azure;

  const grantedPermissions = new Set(
    (azure?.apiPermissions ?? []).map((permission) => permission.toLowerCase()),
  );
  const hasDisclaimerPermission = CUSTOM_DISCLAIMERS_REQUIRED_PERMISSIONS.every((permission) =>
    grantedPermissions.has(permission.toLowerCase()),
  );

  // useGeneralConfig swallows load failures and leaves config null, so that means "unknown".
  let consent: 'checking' | 'unknown' | 'granted' | 'required';
  if (loadingConfig) {
    consent = 'checking';
  } else if (config) {
    consent = hasDisclaimerPermission ? 'granted' : 'required';
  } else {
    consent = 'unknown';
  }

  // The disclaimer list itself is owned by DisclaimerEnrollmentSummaryProvider (one shared
  // request for every consumer); this page only loads policies locally, once the shared list
  // is ready, so it can resolve each disclaimer's policyIds into display names.
  const loadPolicies = useCallback(async () => {
    setPoliciesLoading(true);
    setPoliciesError(null);

    try {
      const policies = await policyService.getPolicies();
      const nextPolicyNamesById = new Map(policies.map((policy) => [policy.id, policy.name]));

      // A rule with a non-empty policyIds list that can't be fully resolved against the
      // policies list is a data-consistency failure, not "no policies assigned" - surfacing it
      // as Unassigned would silently misrepresent the rule, so treat it as a retryable list
      // failure instead. Only a rule with zero source policy ids is genuinely Unassigned.
      const hasUnresolvedPolicyId = disclaimers.some((rule) =>
        rule.policyIds.some((policyId) => !nextPolicyNamesById.has(policyId)),
      );

      if (hasUnresolvedPolicyId) {
        throw new Error('Unable to load custom disclaimers. Please try again.');
      }

      setPolicyNamesById(nextPolicyNamesById);
    } catch (error) {
      setPoliciesError(
        error instanceof Error ? error.message : 'Unable to load custom disclaimers. Please try again.',
      );
    } finally {
      setPoliciesLoading(false);
    }
  }, [disclaimers]);

  useEffect(() => {
    if (consent === 'granted' && disclaimerState === 'ready') {
      loadPolicies();
    }
  }, [consent, disclaimerState, loadPolicies]);

  // The table area reflects whichever of the shared list / local policies request is still in
  // flight or failed. A shared-list failure is retried via the provider's refreshDisclaimers;
  // a local policies failure (including an unresolved policy id) is retried by reloading
  // policies only, since the shared list itself is already fine.
  const tableLoading = disclaimerState === 'loading' || policiesLoading;
  const tableErrorMessage =
    disclaimerState === 'error' ? 'Unable to load custom disclaimers. Please try again.' : policiesError;
  const handleTableRetry = disclaimerState === 'error' ? refreshDisclaimers : loadPolicies;

  const handleAcceptPermissions = async () => {
    await eventTracker.eventTrackOnElementClicked({
      elementType: 'button',
      elementName: ElementButton.ACCEPT_PERMISSIONS,
      pageName: PageName.CUSTOM_DISCLAIMERS,
    });

    if (!azure) {
      return;
    }

    triggerAdminConsent(
      azure.clientId,
      azure.tenantId,
      azure.displayName,
      CUSTOM_DISCLAIMERS_REQUIRED_PERMISSIONS,
    );
  };

  const handleCreateDisclaimer = async () => {
    await eventTracker.eventTrackOnElementClicked({
      elementType: 'button',
      elementName: ElementButton.NEW_POLICY_DISCLAIMER,
      pageName: PageName.CUSTOM_DISCLAIMERS,
    });
    navigate('/custom-disclaimers/new');
  };

  const handleRowNavigate = (ruleId: string) => {
    navigate(`/custom-disclaimers/${ruleId}`);
  };

  const fetchEnrollmentLink = useCallback(async () => {
    const requestToken = ++enrollmentRequestRef.current;

    setEnrollmentLoading(true);
    setEnrollmentError(null);
    setEnrollmentLink(null);
    setCopyError(null);
    setCopySuccessMessage(null);

    try {
      const link = await customDisclaimerService.getDisclaimerEnrollmentLink();
      if (enrollmentRequestRef.current !== requestToken) {
        return;
      }
      setEnrollmentLink(link);
    } catch (error) {
      if (enrollmentRequestRef.current !== requestToken) {
        return;
      }
      // The backend error message isn't user-safe copy, so surface a stable, generic message
      // instead and let the user retry in place via the Try again action.
      setEnrollmentError('Unable to load the enrollment link. Please try again.');
    } finally {
      if (enrollmentRequestRef.current === requestToken) {
        setEnrollmentLoading(false);
      }
    }
  }, []);

  const handleOpenEnrollmentDialog = async () => {
    setIsDialogOpen(true);
    await fetchEnrollmentLink();
  };

  const handleCopyLink = async () => {
    if (!enrollmentLink) {
      return;
    }

    // Clipboard access can be unavailable (insecure context, unsupported browser)
    // or rejected (denied permission), so guard and surface both cases in-dialog.
    if (!navigator.clipboard?.writeText) {
      setCopySuccessMessage(null);
      setCopyError('Copying is not available in this browser. Please try a supported browser.');
      return;
    }

    try {
      await navigator.clipboard.writeText(enrollmentLink);
      setCopyError(null);
      setCopySuccessMessage('Link copied to clipboard.');
    } catch (error) {
      setCopySuccessMessage(null);
      setCopyError(
        error instanceof Error ? error.message : 'Unable to copy the link. Please try again.',
      );
    }
  };

  return (
    <div className={styles.container} data-testid="custom-disclaimers-container-div">
      <div className={styles.header} data-testid="custom-disclaimers-header-div">
        <div className={styles.headerTop} data-testid="custom-disclaimers-header-top-div">
          <Text className={styles.title} data-testid="custom-disclaimers-title-text">
            Disclaimers
          </Text>
        </div>
      </div>

      <div className={styles.content} data-testid="custom-disclaimers-content-div">
        {consent === 'checking' && (
          <div className={styles.loadingContainer} data-testid="custom-disclaimers-loading-div">
            <Spinner label="Loading disclaimers..." />
          </div>
        )}

        {consent === 'unknown' && (
          <MessageBar intent="error" data-testid="custom-disclaimers-consent-error-messagebar">
            <MessageBarBody data-testid="custom-disclaimers-consent-error-body">
              Could not determine whether disclaimers are available.
            </MessageBarBody>
            <MessageBarActions>
              <Button
                appearance="primary"
                size="small"
                onClick={loadConfig}
                data-testid="custom-disclaimers-retry-button"
              >
                Try again
              </Button>
            </MessageBarActions>
          </MessageBar>
        )}

        {consent === 'required' && (
          <div className={styles.setupSection} data-testid="custom-disclaimers-consent-required-div">
            <Text
              className={styles.sectionTitle}
              data-testid="custom-disclaimers-consent-required-title-text"
            >
              Set up disclaimers
            </Text>
            <ul
              className={styles.permissionList}
              data-testid="custom-disclaimers-consent-required-description-text"
            >
              <li>Disclaimers require additional Microsoft Graph permissions.</li>
              <li>A Global Admin must grant these before your organisation can use the feature.</li>
              <li>The permissions let the service apply policy-based disclaimers for ADM policies.</li>
              <li>They're used only for disclaimer delivery and administration, and don't change your existing user access or messaging policies.</li>
            </ul>
            <div
              className={styles.setupActions}
              data-testid="custom-disclaimers-consent-required-actions-div"
            >
              <Button
                appearance="primary"
                className={styles.setupButton}
                onClick={handleAcceptPermissions}
                data-testid="custom-disclaimers-accept-permissions-button"
              >
                Accept permissions
              </Button>
            </div>
          </div>
        )}

        {consent === 'granted' && (
          <div className={styles.section} data-testid="custom-disclaimers-granted-div">
            <div className={styles.headerActions} data-testid="custom-disclaimers-actions-div">
              <Button
                appearance="primary"
                onClick={handleCreateDisclaimer}
                data-testid="custom-disclaimers-new-policy-disclaimer-button"
              >
                + New disclaimer
              </Button>
              <Button
                appearance="secondary"
                onClick={handleOpenEnrollmentDialog}
                data-testid="custom-disclaimers-get-enrollment-link-button"
              >
                Get enrollment link
              </Button>
            </div>
            <Text
              className={styles.sectionDescription}
              data-testid="custom-disclaimers-description-text"
            >
              Configure disclaimers that appear automatically for users based on their policy
              assignment. Changes can take up to 2 hours to take effect.
            </Text>

            {tableLoading && (
              <div
                className={styles.loadingContainer}
                data-testid="custom-disclaimers-rules-loading-div"
              >
                <Spinner label="Loading disclaimers..." />
              </div>
            )}

            {!tableLoading && tableErrorMessage && (
              <MessageBar intent="error" data-testid="custom-disclaimers-rules-error-messagebar">
                <MessageBarBody data-testid="custom-disclaimers-rules-error-body">
                  {tableErrorMessage}
                </MessageBarBody>
                <MessageBarActions>
                  <Button
                    appearance="primary"
                    size="small"
                    onClick={handleTableRetry}
                    data-testid="custom-disclaimers-rules-retry-button"
                  >
                    Try again
                  </Button>
                </MessageBarActions>
              </MessageBar>
            )}

            {!tableLoading && !tableErrorMessage && (
                <div className={styles.tableWrapper} data-testid="custom-disclaimers-table-wrapper">
                  <table
                    className={styles.table}
                    aria-label="Policy disclaimers"
                    data-testid="custom-disclaimers-table"
                  >
                    <colgroup data-testid="custom-disclaimers-table-colgroup">
                      <col style={{ width: '24%' }} data-testid="custom-disclaimers-col-name" />
                      <col style={{ width: '32%' }} data-testid="custom-disclaimers-col-policies" />
                      <col style={{ width: '23%' }} data-testid="custom-disclaimers-col-users-receiving" />
                      <col style={{ width: '21%' }} data-testid="custom-disclaimers-col-last-updated" />
                    </colgroup>
                    <thead>
                      <tr className={styles.tableHeaderRow}>
                        <th>Disclaimer name</th>
                        <th>Policies</th>
                        <th>Users receiving</th>
                        <th>Last updated</th>
                      </tr>
                    </thead>
                    <tbody>
                    {enrollmentState === 'error' && (
                      <tr data-testid="custom-disclaimers-metrics-error-row">
                        <td colSpan={4}>
                          <MessageBar intent="error" data-testid="custom-disclaimers-metrics-error-messagebar">
                            <MessageBarBody data-testid="custom-disclaimers-metrics-error-body">
                              Unable to load users receiving. Please try again.
                            </MessageBarBody>
                            <MessageBarActions>
                              <Button
                                appearance="primary"
                                size="small"
                                onClick={refreshEnrollmentStatus}
                                data-testid="custom-disclaimers-metrics-retry-button"
                              >
                                Try again
                              </Button>
                            </MessageBarActions>
                          </MessageBar>
                        </td>
                      </tr>
                    )}
                    {disclaimers.length === 0 ? (
                      <tr className={styles.emptyTableRow}>
                        <td colSpan={4}>
                          No disclaimers yet. Select <strong>New disclaimer</strong> to create one
                        </td>
                      </tr>
                    ) : (
                      disclaimers.map((rule) => {
                        const matchedNames = rule.policyIds
                          .map((policyId) => policyNamesById.get(policyId))
                          .filter((name): name is string => Boolean(name));
                        const visibleNames = matchedNames.slice(0, MAX_VISIBLE_POLICY_NAMES);
                        const hiddenNames = matchedNames.slice(MAX_VISIBLE_POLICY_NAMES);

                        return (
                          <tr
                            key={rule.id}
                            className={styles.tableRow}
                            data-testid={`custom-disclaimers-row-${rule.id}`}
                          >
                            <td>
                              <button
                                type="button"
                                className={styles.rowNameButton}
                                onClick={() => handleRowNavigate(rule.id)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    handleRowNavigate(rule.id);
                                  }
                                }}
                                data-testid={`custom-disclaimers-row-name-${rule.id}`}
                              >
                                {rule.name}
                              </button>
                            </td>
                            <td>
                              {rule.policyIds.length === 0 ? (
                                <Text data-testid={`custom-disclaimers-row-policies-unassigned-${rule.id}`}>
                                  Unassigned
                                </Text>
                              ) : (
                                <div className={styles.policiesCell}>
                                  <div className={styles.policiesNamesRow}>
                                    <Text data-testid={`custom-disclaimers-row-policies-${rule.id}`}>
                                      {visibleNames.join(', ')}
                                    </Text>
                                    {hiddenNames.length > 0 && (
                                      <Tooltip
                                        content={hiddenNames.join(', ')}
                                        relationship="description"
                                        withArrow
                                      >
                                        <button
                                          type="button"
                                          className={styles.overflowButton}
                                          data-testid={`custom-disclaimers-row-policies-overflow-${rule.id}`}
                                        >
                                          {`+${hiddenNames.length}`}
                                        </button>
                                      </Tooltip>
                                    )}
                                  </div>
                                  {/* Mirrors the Policies assignment-summary secondary line: a
                                      total count always sits beneath the (possibly-truncated)
                                      visible names, matching the shared summary hierarchy. */}
                                  <span
                                    className={styles.policiesTotalLine}
                                    data-testid={`custom-disclaimers-row-policies-total-${rule.id}`}
                                  >
                                    {`${matchedNames.length} polic${matchedNames.length === 1 ? 'y' : 'ies'}`}
                                  </span>
                                </div>
                              )}
                            </td>
                            <td data-testid={`custom-disclaimers-row-users-receiving-${rule.id}`}>
                              {enrollmentState === 'loading' ? (
                                <Spinner size="tiny" label="Loading users receiving" />
                              ) : enrollmentState === 'ready' && usersReceivingByDisclaimerId.has(rule.id) ? (
                                usersReceivingByDisclaimerId.get(rule.id)
                              ) : (
                                '—'
                              )}
                            </td>
                            <td data-testid={`custom-disclaimers-row-updated-${rule.id}`}>
                              {formatUpdatedAt(rule.updatedAt)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                    </tbody>
                  </table>
                </div>
            )}
          </div>
        )}
      </div>

      <Dialog
        open={isDialogOpen}
        onOpenChange={(_, data) => setIsDialogOpen(data.open)}
      >
        <DialogSurface
          className={styles.enrollmentDialogSurface}
          backdrop={{ appearance: 'transparent' }}
          style={{
            width: '600px',
            height: '200px',
            maxWidth: 'calc(100vw - 48px)',
            padding: '23px 23px 28px',
            borderRadius: '8px',
            boxShadow: '0 0 4px rgba(0, 0, 0, 0.2), 0 32px 64px rgba(0, 0, 0, 0.24)',
          }}
          data-testid="custom-disclaimers-enrollment-dialog-surface"
        >
          <DialogBody className={styles.enrollmentDialogBody}>
            <DialogTitle
              className={styles.enrollmentDialogTitle}
              action={
                <DialogTrigger action="close">
                  <Button
                    appearance="subtle"
                    aria-label="Close"
                    icon={<DismissRegular />}
                    data-testid="custom-disclaimers-enrollment-dialog-close-button"
                  />
                </DialogTrigger>
              }
              data-testid="custom-disclaimers-enrollment-dialog-title"
            >
              Enrollment link
            </DialogTitle>
            <DialogContent
              className={styles.enrollmentDialogContent}
              data-testid="custom-disclaimers-enrollment-dialog-content"
            >
              <Text
                className={styles.enrollmentDialogDescription}
                data-testid="custom-disclaimers-enrollment-dialog-description"
              >
                Send this to users who haven&apos;t enrolled yet.
              </Text>
              {enrollmentLoading && (
                <Spinner
                  label="Loading enrollment link..."
                  data-testid="custom-disclaimers-enrollment-loading-spinner"
                />
              )}
              {!enrollmentLoading && enrollmentError && (
                <MessageBar intent="error" data-testid="custom-disclaimers-enrollment-error-messagebar">
                  <MessageBarBody>{enrollmentError}</MessageBarBody>
                  <MessageBarActions>
                    <Button
                      appearance="primary"
                      size="small"
                      onClick={fetchEnrollmentLink}
                      data-testid="custom-disclaimers-enrollment-retry-button"
                    >
                      Try again
                    </Button>
                  </MessageBarActions>
                </MessageBar>
              )}
              {!enrollmentLoading && !enrollmentError && enrollmentLink && (
                <>
                  <Text
                    aria-hidden="true"
                    className={styles.enrollmentLinkText}
                    data-testid="custom-disclaimers-enrollment-link-text"
                  >
                    {getEnrollmentLinkPreview(enrollmentLink)}
                  </Text>
                  <span className={styles.visuallyHidden}>
                    Enrollment link ready. Use Copy link to copy the full link.
                  </span>
                </>
              )}
              {!enrollmentLoading && !enrollmentError && copyError && (
                <MessageBar intent="error" data-testid="custom-disclaimers-copy-error-messagebar">
                  <MessageBarBody>{copyError}</MessageBarBody>
                </MessageBar>
              )}
              <div
                role="status"
                aria-live="polite"
                className={styles.visuallyHidden}
                data-testid="custom-disclaimers-copy-success-status"
              >
                {copySuccessMessage}
              </div>
            </DialogContent>
            <DialogActions className={styles.enrollmentDialogActions}>
              <Button
                appearance="secondary"
                className={styles.enrollmentDialogCancelButton}
                style={{
                  minWidth: 'auto',
                  height: '32px',
                  padding: '5px 12px',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: 600,
                  lineHeight: '20px',
                }}
                onClick={() => setIsDialogOpen(false)}
                data-testid="custom-disclaimers-cancel-dialog-button"
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                className={styles.enrollmentDialogCopyButton}
                style={{
                  width: '102px',
                  minWidth: '102px',
                  maxWidth: '102px',
                  height: '32px',
                  padding: '5px 9px',
                  borderRadius: '4px',
                  backgroundColor: '#5863c0',
                  fontSize: '14px',
                  fontWeight: 600,
                  lineHeight: '20px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
                icon={
                  copySuccessMessage ? (
                    <CheckmarkRegular className={styles.copyLinkIcon} />
                  ) : (
                    <CopyRegular className={styles.copyLinkIcon} />
                  )
                }
                disabled={!enrollmentLink}
                onClick={handleCopyLink}
                data-testid="custom-disclaimers-copy-link-button"
              >
                {copySuccessMessage ? 'Copied' : 'Copy link'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default CustomDisclaimers;
